import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';

import { SubscriptionPackage, SubscriptionPackageHistoryItem, SubscriptionService } from '../../core/services';
import { ConfirmActionModalComponent } from '../../core/ui/confirm-action-modal/confirm-action-modal.component';
import { EntityListComponent, EntityListMenuAction, TableHeaderAndValueKeySets } from '../../core/ui/entity-list/entity-list.component';
import { AddEditPackageComponent, PackageSaveEvent } from './add-edit-package/add-edit-package.component';
import { TranslatePipe } from '../../core/pipes/translate.pipe';

interface PackageRow extends SubscriptionPackage {
  packageTopLine: string;
  packageBottomLine: string;
  storageLabel: string;
  monthlyLabel: string;
  yearlyLabel: string;
}

@Component({
  selector: 'app-package-list',
  standalone: true,
  imports: [CommonModule, EntityListComponent, AddEditPackageComponent, ConfirmActionModalComponent, TranslatePipe],
  templateUrl: './package-list.component.html',
  styleUrls: ['./package-list.component.css', './../../../CSSFiles/BTN_Style.css']
})
export class PackageListComponent implements OnInit {
  isLoading = false;
  isDeleting = false;
  packages: PackageRow[] = [];
  error: string | null = null;
  selectedPackage: SubscriptionPackage | null = null;
  pendingDeletePackage: SubscriptionPackage | null = null;
  selectedPackageHistory: SubscriptionPackageHistoryItem[] = [];
  selectedHistoryItem: SubscriptionPackageHistoryItem | null = null;
  isEditOpen = false;
  isHistoryOpen = false;
  isHistoryDetailsOpen = false;

  readonly packageMenuActions: EntityListMenuAction[] = [
    { key: 'edit', label: 'action.edit' },
    { key: 'delete', label: 'action.delete', danger: true },
    { key: 'history', label: 'action.history' }
  ];

  readonly tableHeaderAndValueKeySets: TableHeaderAndValueKeySets = {
    colorKey: 'status',
    'package.table.name': {
      topLineKey: 'packageTopLine',
      bottomLineKyes: ['packageBottomLine']
    },
    'package.table.flat': 'totalFlat',
    'package.table.renter': 'totalRenter',
    'package.table.subuser': 'maxActiveEmployee',
    'package.table.monthly': 'monthlyLabel',
    'package.table.yearly': 'yearlyLabel'
  };

  constructor(private readonly subscriptionService: SubscriptionService) {}

  ngOnInit(): void {
    this.fetchPackages();
  }

  fetchPackages(): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.subscriptionService.listPackages({
      pagination: true,
      pageNo: 0,
      rowCount: 50,
      filters: {}
    }).pipe(
      finalize(() => (this.isLoading = false))
    ).subscribe({
      next: (res) => {
        if (`${res?.code ?? ''}` !== '000') {
          this.error = `${res?.message ?? 'Failed to fetch package list.'}`;
          return;
        }

        this.packages = (res.data || []).map((item) => this.toRow(item));
      },
      error: (err: any) => {
        this.error = err?.message || 'Failed to fetch package list.';
      }
    });
  }

  onCardClickFromList(item: Record<string, any>): void {
    this.selectedPackage = item as SubscriptionPackage;
    this.isEditOpen = true;
  }

  onEditClickFromList(item: Record<string, any>): void {
    this.selectedPackage = item as SubscriptionPackage;
    this.isEditOpen = true;
  }

  onDeleteClickFromList(item: Record<string, any>): void {
    this.pendingDeletePackage = item as SubscriptionPackage;
  }

  onMenuAction(event: { actionKey: string; item: Record<string, any> }): void {
    if (event.actionKey !== 'history') {
      return;
    }

    const selected = event.item as SubscriptionPackage;
    const packageId = Number(selected?.id);
    if (!Number.isFinite(packageId)) {
      return;
    }

    this.selectedPackage = selected;
    this.selectedPackageHistory = [];
    this.isHistoryOpen = true;

    this.subscriptionService.listPackageHistory({
      pagination: true,
      pageNo: 0,
      rowCount: 100,
      filters: { packageId }
    }).subscribe({
      next: (res) => {
        if (`${res?.code ?? ''}` !== '000') {
          this.error = `${res?.message ?? 'Failed to load package history.'}`;
          return;
        }
        this.selectedPackageHistory = res.data || [];
      },
      error: (err: any) => {
        this.error = err?.message || 'Failed to load package history.';
      }
    });
  }

  closeEditModal(): void {
    this.isEditOpen = false;
    this.selectedPackage = null;
  }

  onEditSave(_: PackageSaveEvent): void {
    this.closeEditModal();
    this.fetchPackages();
  }

  closeDeleteModal(): void {
    if (!this.isDeleting) {
      this.pendingDeletePackage = null;
    }
  }

  closeHistoryModal(): void {
    this.isHistoryOpen = false;
    this.selectedPackageHistory = [];
    this.selectedHistoryItem = null;
    this.isHistoryDetailsOpen = false;
  }

  openHistoryDetails(history: SubscriptionPackageHistoryItem): void {
    this.selectedHistoryItem = history;
    this.isHistoryDetailsOpen = true;
  }

  closeHistoryDetailsModal(): void {
    this.isHistoryDetailsOpen = false;
    this.selectedHistoryItem = null;
  }

  confirmDeletePackage(): void {
    const target = this.pendingDeletePackage;
    const packageId = Number(target?.id);
    if (!Number.isFinite(packageId) || this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    this.subscriptionService.deletePackage(packageId)
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: (res: any) => {
          if (`${res?.code ?? ''}` !== '000') {
            this.error = `${res?.message ?? 'Failed to delete package.'}`;
            this.pendingDeletePackage = null;
            return;
          }
          this.pendingDeletePackage = null;
          this.fetchPackages();
        },
        error: (err: any) => {
          this.error = err?.message || 'Failed to delete package.';
          this.pendingDeletePackage = null;
        }
      });
  }

  private toRow(item: SubscriptionPackage): PackageRow {
    return {
      ...item,
      packageTopLine: item.packageName || 'N/A',
      packageBottomLine: this.formatStorage(item.maxFileMemory),
      storageLabel: this.formatStorage(item.maxFileMemory),
      monthlyLabel: this.formatPrice(item.priceMonthly),
      yearlyLabel: this.formatPrice(item.priceYearly)
    };
  }

  formatPrice(value: number): string {
    const amount = Number(value ?? 0);
    const hasFraction = Math.abs(amount % 1) > 0;
    return `\u09F3${new Intl.NumberFormat('en-BD', {
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0
    }).format(amount)}`;
  }

  formatStorage(value: number): string {
    const memory = Number(value ?? 0);
    if (memory >= 1024) {
      const gb = memory / 1024;
      return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
    }
    return `${memory} MB`;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    const rawHours = date.getHours();
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const period = rawHours >= 12 ? 'pm' : 'am';
    const hours12 = rawHours % 12 || 12;

    return `${day}-${month}-${year} ${`${hours12}`.padStart(2, '0')}:${minutes} ${period}`;
  }

  historySummary(history: SubscriptionPackageHistoryItem): string {
    return `${history.totalFlat || 0}F | ${history.totalRenter || 0}R | ${history.maxActiveEmployee || 0} SU | ${this.formatStorage(history.maxFileMemory)}`;
  }
}
